import { useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ErreurApi, api } from '../lib/apiClient';

/**
 * Ecran bloquant du mot de passe impose (`doit_changer_mdp`).
 *
 * Rendu A LA PLACE du routage : aucune route de l'application n'est
 * atteignable tant que le mot de passe n'a pas ete change — et le
 * serveur refuse de toute facon tout le reste (403 changement_mdp_requis
 * dans requireAuth). Pas de bouton Annuler : la seule autre issue est la
 * deconnexion.
 *
 * Le backend revoque toutes les sessions au changement ; on deconnecte
 * donc le client dans la foulee, et la reconnexion se fait avec le
 * nouveau mot de passe.
 */
export function MotDePasseImposePage() {
  const { session, deconnecter } = useAuth();
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);

    if (nouveau !== confirmation) {
      setErreur('Les deux saisies du nouveau mot de passe different.');
      return;
    }

    if (nouveau.length < 12) {
      setErreur('Le nouveau mot de passe doit comporter au moins 12 caracteres.');
      return;
    }

    setEnCours(true);
    try {
      await api.post('/api/auth/mot-de-passe', {
        motDePasseActuel: actuel,
        nouveauMotDePasse: nouveau,
      });
      await deconnecter();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Changement impossible.');
      setEnCours(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-encre-100 p-4">
      <div className="carte w-full max-w-md p-8">
        <img src="/assets/logo-artistic.jpg" alt="Artistic" className="mx-auto mb-8 h-20 w-auto" />

        <h1 className="mb-1 text-center text-xl font-semibold">Choisissez votre mot de passe</h1>
        <p className="mb-6 text-center text-sm text-encre-500">
          {session?.utilisateur.prenom}, votre mot de passe est temporaire : il doit etre remplace
          avant d’acceder a l’application.
        </p>

        <form onSubmit={soumettre} className="space-y-4">
          <label className="block">
            <span className="etiquette">Mot de passe actuel</span>
            <input
              className="champ"
              type="password"
              value={actuel}
              onChange={(e) => setActuel(e.target.value)}
              autoComplete="current-password"
              required
              autoFocus
            />
          </label>

          <label className="block">
            <span className="etiquette">Nouveau mot de passe</span>
            <input
              className="champ"
              type="password"
              value={nouveau}
              onChange={(e) => setNouveau(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
            <span className="mt-1 block text-xs text-encre-400">12 caracteres minimum.</span>
          </label>

          <label className="block">
            <span className="etiquette">Confirmer le nouveau mot de passe</span>
            <input
              className="champ"
              type="password"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>

          {erreur && (
            <p
              className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
              role="alert"
            >
              {erreur}
            </p>
          )}

          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
            Vous serez ensuite deconnecte, pour vous reconnecter avec le nouveau mot de passe.
          </p>

          <button type="submit" className="bouton-principal w-full" disabled={enCours}>
            {enCours ? 'Changement…' : 'Changer le mot de passe'}
          </button>
        </form>

        <button
          type="button"
          className="mt-4 w-full text-center text-xs text-encre-400 underline underline-offset-2 hover:text-encre-600"
          onClick={() => void deconnecter()}
        >
          Se deconnecter
        </button>
      </div>
    </div>
  );
}
